<template>
  <div class="chat-conv d-flex flex-column">
    <!-- Header -->
    <div class="conv-header pa-3 d-flex align-center">
      <v-avatar size="40" class="mr-3">
        <v-img
          v-if="chatTitle && chatTitle.platformSenderProfilePicture"
          :src="chatTitle.platformSenderProfilePicture"
        />
        <v-icon v-else size="32" color="grey lighten-1">mdi-account-circle</v-icon>
      </v-avatar>
      <div v-if="chatTitle">
        <div class="font-weight-medium">
          {{ chatTitle.platformSenderName || (chatTitle.platformName + "." + chatTitle.chatID) }}
        </div>
        <div class="caption" style="color: grey">
          <v-icon size="12" :color="platformColor">mdi-circle-medium</v-icon>
          {{ use_language.channel }} : {{ platformName }}
        </div>
      </div>
      <div v-else class="caption" style="color: grey">{{ use_language.select_chat_to_start }}</div>
    </div>
    <v-divider />

    <!-- Messages -->
    <div
      ref="scrollEl"
      class="conv-body flex-grow-1"
      @scroll="onScroll"
    >
      <div v-if="loadingHistory" class="py-2 text-center">
        <v-progress-circular indeterminate size="20" color="primary" />
      </div>

      <div
        v-if="!loading && messages.length === 0 && chatId"
        class="text-center py-10"
        style="color: grey"
      >
        <v-icon size="48" color="grey lighten-1">mdi-message-text-outline</v-icon>
        <div class="caption mt-2">{{ use_language.no_messages }}</div>
      </div>

      <div
        v-for="(m, idx) in messages"
        :key="(m.messageId || 'm') + '_' + idx"
        class="msg-row"
        :class="{ 'msg-right': m.sent_by_admin, 'msg-left': !m.sent_by_admin }"
      >
        <div class="msg-bubble">
          <!-- TEXT -->
          <div
            v-if="m.messageType === 'text'"
            class="bubble-content"
            :class="m.sent_by_admin ? 'bubble-admin' : 'bubble-customer'"
            v-html="linkify(m.message, m.sent_by_admin)"
          />

          <!-- IMAGE -->
          <div v-else-if="m.messageType === 'image'" class="bubble-media">
            <div v-if="isLine && m.loadImage === false" class="text-center py-3">
              <v-progress-circular indeterminate color="primary" size="24" />
            </div>
            <img
              v-else
              :src="m.message"
              class="bubble-img"
              loading="lazy"
              @click="onClickImage(m.message)"
              @load="onContentLoad"
            />
          </div>

          <!-- VIDEO -->
          <div v-else-if="m.messageType === 'video'" class="bubble-media">
            <video
              v-if="!isLine"
              :src="m.message"
              controls
              width="240"
              @loadedmetadata="onContentLoad"
            />
            <v-btn v-else small text @click="onClickVideo(m.message)">
              <v-icon left>mdi-play-circle-outline</v-icon>{{ use_language.open_video }}
            </v-btn>
          </div>

          <!-- FILE -->
          <div v-else-if="m.messageType === 'file'" class="bubble-media">
            <a :href="m.message" target="_blank" rel="noopener">
              <v-icon size="20" class="mr-1">mdi-file-document-outline</v-icon>
              {{ use_language.open_file }}
            </a>
          </div>

          <!-- UNKNOWN -->
          <div v-else class="bubble-content bubble-customer">
            [unsupported: {{ m.messageType }}]
          </div>

          <div class="msg-meta">
            <span>{{ formatTime(m.sent_time) }}</span>
            <span v-if="m.sent_by_admin && m.sent_by_staffName">
              · {{ use_language.sent_by }} {{ m.sent_by_staffName }}
            </span>
            <span v-else-if="m.read_by_staffname">
              · {{ use_language.read_by }} {{ m.read_by_staffname }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Image dialog -->
    <v-dialog v-model="imageDialog" max-width="800">
      <v-card>
        <v-card-text class="text-center pa-4">
          <v-img :src="imageDialogSrc" contain max-height="600" />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <a :href="imageDialogSrc" target="_blank" download>
            <v-icon size="18" class="mr-1">mdi-download</v-icon>{{ use_language.download }}
          </a>
          <v-spacer />
          <v-btn text color="primary" @click="imageDialog = false">{{ use_language.close }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Video dialog -->
    <v-dialog v-model="videoDialog" max-width="700">
      <v-card>
        <v-card-text class="pa-4 text-center">
          <video v-if="videoDialogSrc" :src="videoDialogSrc" controls width="100%" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="videoDialog = false">{{ use_language.close }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { expressService_dotnet } from "@/website/global";
import { token_api } from "@/website/backend/token";
import { PLATFORM, PLATFORM_NAME, PLATFORM_COLOR } from "./constants";
import { get_languages } from '@/website/global_function'

const PAGE_SIZE = 10;

export default {
  name: "ChatConversation",
  props: {
    shopId: { type: [String, Number], required: true },
    staffShopId: { type: [String, Number], required: true },
    chatId: { type: [String, Number], default: null },
    platformId: { type: [String, Number], default: null },
    platformConnectionID: { type: [String, Number], default: null },
    chatTitle: { type: Object, default: null },
  },
  data: () => ({
    messages: [],
    loading: false,
    loadingHistory: false,
    eof: false,
    imageDialog: false,
    imageDialogSrc: "",
    videoDialog: false,
    videoDialogSrc: "",
    use_language: {},
  }),
  computed: {
    isLine() {
      return parseInt(this.platformId) === PLATFORM.LINE;
    },
    platformName() {
      return PLATFORM_NAME[this.platformId] || "";
    },
    platformColor() {
      return PLATFORM_COLOR[this.platformId] || "grey";
    },
  },
  watch: {
    chatId(val) {
      this.messages = [];
      this.eof = false;
      if (val) this.loadMessages(true);
    },
  },
  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
  },
  methods: {
    get_languages,
    formatTime(t) {
      if (!t) return "";
      const s = String(t);
      if (s.includes("T")) {
        const d = new Date(s);
        if (!isNaN(d.getTime())) {
          return d.toLocaleString("th-TH", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }
      return s;
    },
    linkify(text, byAdmin) {
      if (!text) return "";
      const re = /((https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?)/gi;
      return text.replace(re, (url) => {
        const safe = url.startsWith("http") ? url : "http://" + url;
        return `<a href="${safe}" target="_blank" class="${byAdmin ? 'white--text' : ''}">${url}</a>`;
      });
    },
    onClickImage(src) {
      this.imageDialogSrc = src;
      this.imageDialog = true;
    },
    onClickVideo(src) {
      this.videoDialogSrc = src;
      this.videoDialog = true;
    },

    async loadMessages(reset = false) {
      if (!this.chatId) return;
      if (reset) this.loading = true;
      else this.loadingHistory = true;

      try {
        const skip = reset ? 0 : this.messages.length;
        const res = await axios.post(
          expressService_dotnet + "CoreMessage/get-chat-message-detail-asyncs",
          {
            shopId: this.shopId,
            chatId: this.chatId,
            staffShopId: this.staffShopId,
            skip,
            take: PAGE_SIZE,
          },
          { headers: { "Content-Type": "application/json", Authorization: "Bearer " + token_api } }
        );
        let data = (res.data && res.data.messages) || [];
        // backend ส่ง latest-first → reverse ให้เก่า…ใหม่ (ตามเวลา)
        data = data.slice().reverse();

        if (data.length < PAGE_SIZE) this.eof = true;

        if (reset) {
          this.messages = data;
          this.$nextTick(() => this.scrollToBottom());
        } else {
          // เก็บ scroll position ก่อน prepend
          const el = this.$refs.scrollEl;
          const prevH = el ? el.scrollHeight : 0;
          const prevT = el ? el.scrollTop : 0;
          this.messages = data.concat(this.messages);
          this.$nextTick(() => {
            if (el) el.scrollTop = prevT + (el.scrollHeight - prevH);
          });
        }

        // LINE image: fetch base64 asynchronously
        if (this.isLine) this._fetchLineMediaForRecent(data);
      } catch (err) {
        this.$swal && this.$swal({
          icon: "error",
          title: this.use_language.load_messages_failed,
          text: err.message || this.use_language.error,
        });
      } finally {
        this.loading = false;
        this.loadingHistory = false;
      }
    },

    _fetchLineMediaForRecent(batch) {
      // parallel fetch — ไม่รอตามลำดับ (เร็วกว่า sequential หลายเท่า)
      const targets = (batch || []).filter((m) => m.messageType === "image" && !m.loadImage);
      const headers = { "Content-Type": "application/json", Authorization: "Bearer " + token_api };
      targets.forEach((t) => {
        const url =
          expressService_dotnet +
          "LineMessage/get-content-by-messageid?PlatformConnectionID=" +
          this.platformConnectionID +
          "&messageId=" +
          t.messageId;
        axios.post(url, {}, { headers })
          .then((res) => {
            const base64 = "data:image/jpg;base64," + res.data;
            const idx = this.messages.findIndex((m) => m.messageId === t.messageId);
            if (idx >= 0) {
              this.$set(this.messages, idx, { ...this.messages[idx], message: base64, loadImage: true });
            }
          })
          .catch((e) => console.error("[line-media] failed", t.messageId, e));
      });
    },

    onScroll(e) {
      const el = e.target;
      if (el.scrollTop === 0 && !this.eof && !this.loadingHistory) {
        this.loadMessages(false);
      }
    },

    scrollToBottom() {
      const el = this.$refs.scrollEl;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },

    onContentLoad() {
      // re-snap to bottom if user is already near bottom
      const el = this.$refs.scrollEl;
      if (!el) return;
      const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 200;
      if (nearBottom) this.scrollToBottom();
    },

    // ===== Public methods =====
    /** เพิ่ม message ใหม่ (จาก WebSocket หรือจาก optimistic send) */
    appendMessage(msg) {
      const el = this.$refs.scrollEl;
      const wasAtBottom = el ? el.scrollHeight - (el.scrollTop + el.clientHeight) < 80 : true;
      this.messages.push(msg);
      this.$nextTick(() => {
        if (wasAtBottom) this.scrollToBottom();
      });
    },
    /** scroll ลงล่างสุด */
    snapToBottom() {
      this.$nextTick(() => this.scrollToBottom());
    },
    /** mark-read (เรียกตอนรับ socket new message + อยู่ chat นั้น) */
    async markRead(messageId) {
      try {
        await axios.post(
          expressService_dotnet + "CoreMessage/mark-read",
          {
            shopId: this.shopId,
            chatId: this.chatId,
            staffShopId: this.staffShopId,
            messageId,
          },
          { headers: { "Content-Type": "application/json", Authorization: "Bearer " + token_api } }
        );
      } catch (e) {
        console.error("[mark-read] failed", e);
      }
    },
  },
};
</script>

<style scoped>
.chat-conv {
  height: 100%;
  background: #fafafa;
}
.conv-header {
  background: #fff;
}
.conv-body {
  overflow-y: auto;
  padding: 12px;
}
.msg-row {
  display: flex;
  margin-bottom: 10px;
}
.msg-row.msg-right { justify-content: flex-end; }
.msg-row.msg-left { justify-content: flex-start; }
.msg-bubble { max-width: 70%; }
.bubble-content {
  padding: 8px 12px;
  border-radius: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
}
.bubble-admin {
  background: #1976d2;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.bubble-customer {
  background: #eceff1;
  color: #212121;
  border-bottom-left-radius: 4px;
}
.bubble-media .bubble-img {
  border-radius: 8px;
  cursor: pointer;
  max-width: 240px;
  max-height: 240px;
  display: block;
  object-fit: contain;
}
.msg-meta {
  font-size: 10px;
  color: #9e9e9e;
  margin-top: 4px;
}
.msg-right .msg-meta { text-align: right; }
</style>
